import React, { useState, useEffect } from 'react';
import Image from '@atoms/Image';
import SvgIconBox from '@atoms/SvgIconBox';
import PollWithOptions from '../../public/images/InteractionPage/dynamic-svg/poll.svg';
import MapIcon from '../../public/images/InteractionPage/dynamic-svg/map.svg';
import WordcloudIcon from '../../public/images/InteractionPage/dynamic-svg/wordcloud.svg';
import PollIcon from '../../public/images/InteractionPage/dynamic-svg/pollff.svg';
import CircleIcon from '../../public/images/InteractionPage/dynamic-svg/circle.svg';
import ThoughtsIcon from '../../public/images/InteractionPage/dynamic-svg/thoughts.svg';
import WheelIcon from '../../public/images/InteractionPage/dynamic-svg/wheel.svg';
import QuickQuestionIcon from '../../public/images/InteractionPage/dynamic-svg/questions.svg';
import LinkLibraryIcon from '../../public/images/InteractionPage/dynamic-svg/link-library.svg';
import EmojisEverywhereIcon from '../../public/images/InteractionPage/dynamic-svg/emojiEverywhere.svg';
import TextTrackIcon from '../../public/images/InteractionPage/dynamic-svg/text-track.svg';
import TalkingTilesIcon from '../../public/images/InteractionPage/dynamic-svg/talkingTiles.svg';
import RatingPollsIcon from '../../public/images/InteractionPage/dynamic-svg/pulsing-points.svg';
import MultipleChoiceIcon from '../../public/images/InteractionPage/dynamic-svg/multiple-choice-colored.svg';
import { getLiveLayoutColors } from './helper/presentationLive';
import useFonts from '@lib/hooks/useFonts';
import { UsersThreeIcon as Groups2RoundedIcon } from '@phosphor-icons/react';

const InteractionTitle = ({
  title,
  titleFont = 'Sans-Serif',
  titleColor = '#FFFFFF',
  type,
  logoUrl,
  slideUrl,
  isPreview = false,
  isLogoRequired = false,
  showGrid = false,
  layoutTheme = 'dark'
}) => {
  const { getFontStyle } = useFonts();
  const [fontStyle, setFontStyle] = useState(getFontStyle(titleFont));
  const colors = getLiveLayoutColors(layoutTheme);

  // Calculate font size based on word count
  const calculateFontSize = (text) => {
    if (!text) return 'text-5xl';

    const wordCount = text.trim().split(/\s+/).filter(word => word.length > 0).length;

    if (wordCount <= 30) {
      return 'text-5xl'; // Large - up to 30 words
    } else if (wordCount <= 40) {
      return 'text-4xl'; // Medium - 30-40 words
    } else {
      return 'text-3xl'; // Small - 40+ words
    }
  };

  const fontSize = calculateFontSize(title);

  const createInteraction = (icon, color, text1, interactionTitle) => {
    const commonProps = {
      color: `text-interactionIcons-${color}`,
      text1,
      interactionTitle
    };

    if (icon) {
      commonProps.icon = React.cloneElement(icon, {
        width: '26',
        height: '26',
        className: `fill-current text-interactionIcons-${color}`
      });
    }

    return commonProps;
  };

  const interactionsMap = {
    pollWithOptions: createInteraction(<PollWithOptions />, 'PollWithOptions', 'Power Poll With Options', title),
    map: createInteraction(<MapIcon />, 'MagicMap', 'Magic Maps', title),
    wordCloud: createInteraction(<WordcloudIcon />, 'WonderWords', 'Wonder Words', title),
    freeForm: createInteraction(<PollIcon />, 'PollWithOutOptions', 'Power Poll Without Options', title),
    wheel: createInteraction(<span style={{ width: '32px', height: '32px' }}><CircleIcon /></span>, 'ChoiceCircle', 'Choice Circle', title),
    transientThought: createInteraction(<ThoughtsIcon />, 'TransientThoughts', 'Transient Thoughts', title),
    newWheel: createInteraction(<span style={{ width: '32px', height: '32px' }}><WheelIcon /></span>, 'WinnersWheel', 'Winning Wheel', title),
    emojisEverywhere: createInteraction(<EmojisEverywhereIcon />, 'EmojisEverywhere', 'Emojis Everywhere', title),
    talkingTiles: createInteraction(<TalkingTilesIcon />, 'TalkingTiles', 'Talking Tiles', title),
    'Quick Questions': createInteraction(<QuickQuestionIcon />, 'QuickQuestions', 'Quick Questions', title),
    linkLibrary: createInteraction(<LinkLibraryIcon />, 'linkLibrary', 'Link Library', title),
    textTrack: createInteraction(<TextTrackIcon />, 'TextTrack', 'Text Track', title),
    'PDF Slide': createInteraction(null, 'PdfSlide', 'Slide', title),
    ratingPolls: createInteraction(<RatingPollsIcon />, 'ratingPolls', 'Rating Polls', title),
    fantasticFans: createInteraction(<Groups2RoundedIcon />, 'FantasticFans', 'Fantastic Fans', title),
    multipleChoice: createInteraction(<span className='h-7 w-7 flex justify-center items-center'><MultipleChoiceIcon /></span>, 'MultipleChoice', 'Multiple Choice', title),
    quiz: createInteraction(<span className='h-7 w-7 flex justify-center items-center'><MultipleChoiceIcon /></span>, 'Quiz', 'Quiz', title),
    leaderboard: createInteraction(<span className='h-7 w-7 flex justify-center items-center'><MultipleChoiceIcon /></span>, 'Leaderboard', 'Leaderboard', title),
    credits: createInteraction(<Groups2RoundedIcon />, 'FantasticFans', 'End Credits', title),
  };

  useEffect(() => {
    setFontStyle(getFontStyle(titleFont));
  }, [titleFont]);

  if (!type || !interactionsMap[type]) return null;

  const {
    color, icon, text1, interactionTitle
  } = interactionsMap[type];

  return (
    <>
      {showGrid ? <div className='w-full'>
        {isPreview && type === 'PDF Slide' ? <Image src={slideUrl} className={`w-[200px] h-[113.23px] shrink-0 border border-neutral-10  rounded-lg  ${['dummy-image', 'failed-image'].some((substring) => slideUrl.includes(substring)) && 'object-cover'}`} /> : <SvgIconBox svg={icon} className={`w-[200px] h-[113.23px] ${colors.containerColor} shrink-0 border border-neutral-10 rounded-lg`} disablePointer={true} />}

        <div className='flex flex-col py-3'>
          {isPreview && <div className={`${color} text-center font-Inter font-medium text-xs leading-4`}>{text1}</div>}

          <SvgIconBox
            showDarkToolTip={true}
            id='interaction-title-tooltip'
            toolTipText={interactionTitle.replace(/(.{26})/g, '$1-')}
            place='bottom'
            className={'border-focused w-full h-full'}
            disablePointer={true}
            text={<div className='w-full text-center truncate overflow-hidden'>{interactionTitle}</div>}
          />
        </div>

      </div>
        : <div className='flex justify-center items-start gap-x-6'>
          <div
            style={fontStyle}
            className='flex flex-col grow'>
            {isPreview && <div className={`${color}  font-medium text-xs leading-4`}>{text1}</div>}

            {interactionTitle
              && (
                <p style={{ color: titleColor, whiteSpace: 'pre-wrap' }} className={`${!isPreview ? fontSize : 'text-base'} dark:text-white font-semibold py-1 break-words leading-tight!`}>
                  {interactionTitle}
                </p>
              )}
          </div>
          <div className="relative flex shrink-0 h-24">
            {isLogoRequired && (<img
              className="inline-block h-full"
              src={logoUrl}
            />
            )}
          </div>
        </div>
      }
    </>);
};

export default InteractionTitle;
